// ============================================================
// How a marker crosses the map: reach, routes, storeys, climbs
// ============================================================
// Pure. The hooks in useMovement.js memoize these on their inputs; every
// rule about movement lives in engine/battlefield and this file only
// decides which question to ask it for the marker being moved.

import {
  reachableFrom,
  movementBudget,
  pathTo,
  pathTerrain,
  pathVertical,
  tileKey,
  MOVEMENT_MODES,
} from '../../engine/battlefield/movement';
import { terrainRulesFor, traitIdsOf } from '../../engine/battlefield/traitRules';
import { climbCostFor, climbRateNote, climbRateFacts, climbCharge } from '../../engine/battlefield/climb';
import { levelsAt } from '../../engine/battlefield/levels';
import { bandFor } from '../../engine/battlefield/bands';
import { groundDistance } from '../../engine/battlefield/measure';
import { projectDefender } from '../../engine/roster/projection';
import { movementUnder } from '../../engine/conditionMovement';
import { EMPTY, EMPTY_OVERLAPS } from './panelDefaults';
import { withoutOwnBox } from '../../engine/battlefield/ownBox';

/**
 * How far a marker drag floods for a route when no movement action is
 * chosen, in metres. Comfortably past anything anyone drags in one go,
 * and a small fraction of a map's diagonal.
 */
const DEFAULT_DRAG_REACH = 30;

/** The bands Auto colours, nearest first. */
const BAND_KEYS = MOVEMENT_MODES;

/**
 * AUTO IS NOT A BUDGET: it measures against the furthest band, Run, and
 * the label names the action instead.
 */
const budgetFor = (movement, mode) => movementBudget(movement, mode === 'auto' ? 'run' : mode);

/**
 * What each combatant's own nature says about crossing ground, as a lookup
 * by id. Flyer, Hoverer and Burrower said "movement reminder only" in the
 * catalogue long before there was a map to remind anyone about. The GM's
 * manual switch still wins - the trait sets the default.
 */
export function rulesByCombatant(roster) {
  const byId = {};
  for (const combatant of roster) {
    byId[combatant.id] = terrainRulesFor(traitIdsOf(combatant.sheet), projectDefender(combatant)?.size);
  }
  return (id) => byId[id] || terrainRulesFor([]);
}

/**
 * How this token crosses the map: its traits, plus whatever was ticked.
 *
 * v8.199.4: the token's own flag is SIGHT-ONLY now ("Ignores LoS") -
 * movement exemptions are the table-wide Terrain toggle and the movement
 * traits (Flyer/Phase/Incorporeal) via `rulesFor`.
 */
export function walkOf(
  token,
  { rulesFor, ignoreTerrain, solids, isGm, groundAt, climbCosts, climbTraining, climbSkillOf },
) {
  const rules = rulesFor(token?.id);
  const free = ignoreTerrain || rules.ignoresTerrain;
  return {
    // Never its own box (2026-10-26): an Object the GM drags starts inside the box it is.
    solids: free ? EMPTY : withoutOwnBox(solids, token?.id),
    options: {
      includeHidden: isGm,
      ignoresGoing: rules.ignoresGoing,
      maxStep: rules.maxStep,
      groundAt,
      // What a metre of rise costs this one. A hoverer is zeroed inside the
      // fill, and a flyer never gets here - their fill has no solids in it,
      // so there is no rise to charge for.
      climbCost: climbCosts ? climbCostFor(climbSkillOf(token?.id), climbTraining) : 0,
      // WHICH STOREY THIS FLOOD IS ON. Without it the fill took the surface
      // at the starting column, so a marker standing on a gantry got their
      // bands painted on the floor below them.
      startY: token?.elevation ?? 0,
    },
    free,
  };
}

/**
 * WHERE A DRAG ACTUALLY ENDS UP: flooded once when the drag starts, then
 * asked for the last tile of the route to wherever the pointer is. So a
 * marker walks round a wall, climbs a crate a step at a time, and stops
 * rather than passing through anything.
 *
 * Null for anyone exempt - flying, phasing, or terrain switched off - and
 * then the pointer is obeyed exactly and the marker STAYS wherever it is
 * put, which is the other half of the terrain-ignore round trip.
 */
export function dragRouteFor(token, { walkFor, movingId, movingMovement, movementMode, groundAt, boundary, tile }) {
  const walk = walkFor(token);
  if (walk.free) return null;
  // BOUNDED BY THE MOVER'S OWN REACH, not by the map. This flooded the whole
  // arena's diagonal on every drag start (v7.33.0). Twice the chosen
  // action, or a sensible default when none is chosen, is more than anyone
  // drags in one go.
  const chosen = movementBudget(movingMovement, movementMode);
  // PLUS THE WAY DOWN (v8.7.0). A drop from a tall roof can cost more than
  // the flood covers - twenty metres untrained is sixty - and without this
  // the route to the ground would not EXIST. It is for markers PUT up there
  // (deployed onto a roof, or left on one when terrain loaded under them),
  // who have no other way down; whether the way down is AFFORDABLE stays
  // the label's business, and the label turns red.
  const drop = Math.max(0, (token.elevation ?? 0) - (groundAt ? groundAt(token.x, token.z) : 0));
  const budget = Math.max(chosen * 2, DEFAULT_DRAG_REACH) + drop * (walk.options.climbCost ?? 0);
  // As far as the route will want, for the one whose route is drawn: it finds this flood made (floodFrom).
  const wide = token.id === movingId ? Math.max(budget, routeRoom(movingMovement, movementMode) * HEADROOM) : budget;
  const tiles = floodFrom(token, { need: budget, wide }, boundary, walk);
  const byKey = new Map(tiles.filter((t) => t.cost <= budget + 1e-9).map((t) => [tileKey(t.x, t.z, tile), t]));
  return (x, z) => byKey.get(tileKey(x, z, tile)) ?? null;
}

/**
 * WHAT THEY CAN ACTUALLY MANAGE, given the state they are in. Conditions
 * govern how far you get exactly as they govern your tests - behind the
 * same house rule.
 */
export function movementOf(movingId, sheetMovement, { roster, applyConditionModifiers, fatigueSlowsMovement }) {
  const unchanged = () => ({ movement: sheetMovement, reasons: [], stopped: false });
  if (!movingId || !applyConditionModifiers) return unchanged();
  // GUARD THE ARGUMENT, not the result. `projectDefender` reads `c.sheet`
  // before anything optional can help, so a combatant who is not in the
  // roster - a selection made before the roster has synced, which is
  // exactly what a second client does on joining - took the whole panel
  // down with it.
  const combatant = roster.find((c) => c.id === movingId);
  if (!combatant) return unchanged();
  return movementUnder(sheetMovement, projectDefender(combatant).conditions ?? [], {
    fatigueSlows: fatigueSlowsMovement,
  });
}

/**
 * The storeys of the column the moving marker is standing in. Flying,
 * phasing and terrain-off are all `walk.free`, exactly the set who choose
 * their own altitude, so the picker asks the same question the drag does.
 */
export function levelsFor(movingToken, solids, groundAt, walkFor) {
  if (!movingToken) return EMPTY;
  const walk = walkFor(movingToken);
  return levelsAt(movingToken.x, movingToken.z, withoutOwnBox(solids, movingToken.id), {
    floor: groundAt(movingToken.x, movingToken.z),
    from: movingToken.elevation ?? 0,
    maxStep: walk.options.maxStep,
    climbs: true,
    flies: walk.free,
    descends: true,
  });
}

/**
 * WHAT EACH STOREY COSTS TO REACH, through the same `climbCharge` the
 * flood fill uses, so a row and a drag can never disagree. Coming DOWN is
 * free here and charged there, deliberately: choosing a storey is a
 * placement, and a free way down is what stops anyone being stranded.
 */
export function levelCostsFor({
  movingToken,
  levels,
  levelPickerSpends,
  climbCosts,
  climbTraining,
  climbSkillOf,
  rulesFor,
  movementMode,
  movingMovement,
}) {
  if (!movingToken || !levelPickerSpends || !climbCosts) return EMPTY_OVERLAPS;
  const stride = rulesFor(movingToken.id).maxStep;
  const rate = climbCostFor(climbSkillOf(movingToken.id), climbTraining);
  const allowed = movementMode ? budgetFor(movingMovement, movementMode) : 0;
  const out = {};
  for (const level of levels) {
    const rise = level.top - movingToken.elevation;
    // Down is free; only a climb has a price on it.
    const cost = rise > 0 ? climbCharge(rise, stride) * rate : 0;
    out[level.top] = { cost, blocked: cost > 0 && allowed > 0 && cost > allowed + 1e-9 };
  }
  return out;
}

/**
 * The tiles within reach. AUTO FLOODS ONCE, AT THE FURTHEST BUDGET: every
 * tile carries the cost of reaching it, so the bands are a colour per tile
 * rather than four separate fills.
 */
export function reachFor({ movementMode, movingToken, movingId, movingMovement, dragOrigin, boundary, walkFor }) {
  if (!movementMode || !movingToken) return EMPTY;
  const budget = budgetFor(movingMovement, movementMode);
  if (!budget) return EMPTY;
  const walk = walkFor(movingToken);
  // FROZEN FOR THE DRAG, and only for the drag: while somebody is carried
  // the disc stays where they set off from, so it shows what the move is
  // costing; the moment they are put down it re-centres on them (v7.44.0
  // anchored it harder and left it stranded).
  const from = dragOrigin?.tokenId === movingId ? dragOrigin : movingToken;
  return reachableFrom({ x: from.x, z: from.z }, budget, boundary, walk.solids, walk.options);
}

/**
 * The cost at which each band ends, furthest last. Only in Auto: a
 * single-band mode is already the answer to its own question.
 */
export function movementBandsFor(movementMode, movingMovement) {
  return movementMode === 'auto' ? bandsOf(movingMovement) : null;
}

/** The bands a movement allows, each with its limit; none for an unknown movement. */
const bandsOf = (movement) =>
  BAND_KEYS.map((key) => ({ limit: movementBudget(movement, key), key })).filter((b) => b.limit > 0);

/**
 * ONE FLOOD PER DRAG (the perf overhaul, Stage 1). The route was re-flooded on every pointer move - its room grows
 * with the carry - and that flood was 3.4 s of a 6 s drag on C6, the drag's stalls. A flood is kept and asked again
 * for each path while it still reaches; carried past it, it is made again HEADROOM times as far, so a long carry
 * floods a few times, not every frame. A wider flood finds the same shortest path to any point inside the narrower
 * one.
 *
 * AND ONE FLOOD PER PICK-UP (2026-10-30). The carry's lookup (dragRouteFor) and the route each flooded from where the
 * figure stood: 50 and 86 ms of a 413 ms pick-up at 4x. The floods are kept here against the spot, the storey and
 * the ground they were made on - not the drag - and the lookup floods as far as the route will want, so the route
 * finds it made. A few are kept: the last drags' spots, never a whole evening's.
 */
const HEADROOM = 1.5;
const KEPT_FLOODS = 4;
const kept = [];

const sameList = (a, b) => a.length === b.length && a.every((x, i) => Object.is(x, b[i]));
const sameOptions = (a, b) =>
  Object.keys(a).length === Object.keys(b).length && Object.keys(a).every((k) => Object.is(a[k], b[k]));
const fits = (k, { from, need, boundary, solids, options }) =>
  k.x === from.x &&
  k.z === from.z &&
  k.boundary === boundary &&
  k.reach >= need &&
  sameList(k.solids, solids) &&
  sameOptions(k.options, options);

/** Every tile within at least `need` of `from` (a kept flood, or a new one `wide` far); never fewer. */
function floodFrom(from, { need, wide }, boundary, { solids, options }) {
  const hit = kept.find((k) => fits(k, { from, need, boundary, solids, options }));
  if (hit) return hit.tiles;
  const tiles = reachableFrom({ x: from.x, z: from.z }, wide, boundary, solids, options);
  kept.unshift({ x: from.x, z: from.z, boundary, reach: wide, solids, options, tiles });
  kept.length = Math.min(kept.length, KEPT_FLOODS);
  return tiles;
}

/** How far the route looks at the start of a carry: the budget, then as far again to show it overspent. */
const routeRoom = (movement, mode) => 2 * budgetFor(movement, mode);

function dragFlood(dragOrigin, room, boundary, walk) {
  // FROM THE STOREY THE DRAG BEGAN ON. `walkFor` reads the token's CURRENT
  // elevation, which changes as it is carried - so a marker walked down off
  // a platform was re-flooded from inside the platform (v8.12.1).
  const options = { ...walk.options, startY: dragOrigin.y ?? walk.options.startY };
  return floodFrom(dragOrigin, { need: room, wide: room * HEADROOM }, boundary, { solids: walk.solids, options });
}

/**
 * The route the token being dragged is taking, and what it costs. Flooded
 * from where the drag STARTED, well past the budget, so going too far can
 * be shown rather than prevented. Only for the token whose reach is on
 * screen: a route with no budget beside it says nothing.
 */
export function routeVectorFor({ dragOrigin, movementMode, movingId, movingMovement, tokens, boundary, walkFor }) {
  if (!dragOrigin || !movementMode || dragOrigin.tokenId !== movingId) return null;
  const token = tokens[dragOrigin.tokenId];
  const budget = budgetFor(movingMovement, movementMode);
  if (!token || !budget) return null;
  const room = routeRoom(movingMovement, movementMode) + Math.hypot(token.x - dragOrigin.x, token.z - dragOrigin.z);
  const tiles = dragFlood(dragOrigin, room, boundary, walkFor(token));
  const path = pathTo(tiles, { x: token.x, z: token.z }, boundary);
  return { path, budget, crossed: pathTerrain(path), vertical: pathVertical(path) };
}

/** A route that neither climbs nor drops by more than a few centimetres. */
const isFlat = (v) => !v || (v.climbed <= 0.05 && v.dropped <= 0.05);

/**
 * The climb rate, in pieces, WHILE A ROUTE IS ACTUALLY CLIMBING. Null the
 * rest of the time, which is most of the time.
 */
export function climbFactsFor(
  vector,
  movingId,
  { tokens, walkFor, climbSkillOf, climbCosts, climbTraining, rulesFor },
) {
  if (!movingId || isFlat(vector?.vertical)) return null;
  const token = tokens[movingId];
  if (!token || walkFor(token).free) return null; // a flyer never meets the question
  return climbRateFacts(climbSkillOf(movingId), {
    costs: climbCosts,
    trainingCounts: climbTraining,
    maxStep: rulesFor(movingId).maxStep,
  });
}

/**
 * What the Legend's climbing page should say about `who` - whoever is
 * being moved, or the actor. Useful with nobody at all, since the rates
 * are the same for everybody; only the last line is personal.
 */
export function legendClimbFor(who, { rulesFor, climbSkillOf, climbCosts, climbTraining }) {
  return {
    maxStep: who ? rulesFor(who).maxStep : undefined,
    rate: who && climbCosts ? climbCostFor(climbSkillOf(who), climbTraining) : undefined,
    costs: climbCosts,
    trainingCounts: climbTraining,
  };
}

/** The log entry for a climb of `rise` metres by `id`, or null for a step. */
function climbEntry(id, storeys, { rulesFor, climbSkillOf, climbCosts, climbTraining, names, tokens }) {
  const rise = (storeys.toY ?? 0) - (storeys.fromY ?? 0);
  const stride = rulesFor(id).maxStep;
  if (Math.abs(rise) <= (Number.isFinite(stride) ? stride : Infinity)) return null;
  const rate = climbCosts ? climbCostFor(climbSkillOf(id), climbTraining) : 0;
  return {
    id,
    name: names[id] || id,
    metres: Math.abs(rise),
    up: rise > 0,
    spent: storeys.free ? 0 : climbCharge(rise, stride) * rate,
    rung: climbRateNote(climbSkillOf(id), { costs: climbCosts, trainingCounts: climbTraining, maxStep: stride }),
    x: tokens[id]?.x,
    z: tokens[id]?.z,
  };
}

/**
 * The pace a move is logged at (engine/moveBeat): the action chosen, else
 * - Auto, or none chosen - the band the distance falls in, as Auto's label
 * names it; past a run, a run. A mover whose movement is unknown walks.
 */
export function modeFor(movementMode, metres, movement) {
  if (BAND_KEYS.includes(movementMode)) return movementMode;
  const bands = bandsOf(movement);
  if (!bands.length) return 'half';
  return bandFor(metres, bands) ?? 'run';
}

/**
 * What a finished move was, for its line: the route the map drew for the
 * carry when it drew one (its cost is the metres, as the label said), else
 * the straight line.
 */
function moveFacts(id, { from, to }, ctx) {
  const route = ctx.vector?.path ?? [];
  const metres = route.length ? route[route.length - 1].cost : groundDistance(from, to);
  return {
    actorId: id,
    name: ctx.names?.[id] || id,
    from,
    to,
    path: route.slice(1, -1).map((t) => ({ x: t.x, z: t.z, y: t.y })),
    // A Pinned figure's crawl to cover says so (2026-10-25); otherwise the band it fell in.
    mode: ctx.crawling === id ? 'crawl' : modeFor(ctx.movementMode, metres, movementOfMover(id, ctx)),
    metres,
  };
}

/**
 * The mover's movement under its conditions, off the roster - whoever moved, as `useMovingSelection` reads the
 * selected one's (the Track M review: an unselected Ork dragged 22 m was logged as a half move).
 */
function movementOfMover(id, ctx) {
  const sheet = ctx.roster?.find((c) => c.id === id)?.sheet?.movement;
  return sheet && movementOf(id, sheet, ctx).movement;
}

/**
 * A marker finished a move: tell whoever checks kill zones, SAY SO AS ONE
 * PUBLIC LINE (plan design-gaps, M2: the walk every screen plays), and SAY
 * SO WHEN SOMEBODY CLIMBS.
 *
 * The move's line goes for every move across the ground, on the client
 * that made it - never a hidden figure's, whose route would reach the
 * players as its token never does; a storey picked from the column is not
 * a move across the ground. The climb's note only when the vertical is
 * worth a sentence - more than one stride, the same allowance that makes a
 * step free. The elevation the gesture STARTED at, not the route's total:
 * a marker that went up a crate and down the far side finished level, and
 * what a table wants recorded is that somebody is now on the gantry.
 */
export function reportMove(id, storeys, ctx) {
  // EVERY completed move, climb or not, and only on the client that made
  // it: this is where a kill zone gets checked (v8.308.0) - a carry out of
  // cover and back is still a move through it.
  ctx.onTokenSettled?.(id);
  if (!storeys) return;
  const moved = storeys.from && storeys.to && !backWhereItWas(storeys);
  // What the move does to the mover (Ran, a lost aim, leaving melee: 2026-10-26) - a hidden figure's too.
  const facts = moved ? moveFacts(id, storeys, ctx) : null;
  if (facts) ctx.onMoveSettled?.(facts);
  if (facts && !ctx.hiddenIds?.has(id)) ctx.onMoveMade?.(facts);
  if (!ctx.onMove) return;
  const entry = climbEntry(id, storeys, ctx);
  if (entry) ctx.onMove(entry);
}

/** Under this far from where a carry began, on the same level, a put-down says no move, metres. */
const NO_MOVE_M = 0.05;

/**
 * Did the carry end where it began - the same spot give or take a few centimetres, and the same height? Then no
 * move line goes in the log (Mission 6's log was full of "moves 0.0 m"); the kill zones are still asked.
 */
const backWhereItWas = ({ from, to }) =>
  Math.hypot(to.x - from.x, to.z - from.z) < NO_MOVE_M && Math.abs((to.y ?? 0) - (from.y ?? 0)) < NO_MOVE_M;

/**
 * Put the moving marker on that storey.
 *
 * IT USED TO BE A PLACEMENT, "like the wheel - it spends nothing". The
 * wheel moves a flyer through air it is entitled to; this moved a man up a
 * wall the movement rules charge for, so reaching a roof cost eight metres
 * by drag and nothing by list (v8.11.0). There is no ledger to deduct
 * from: charged means PRICED on the row, GATED against the chosen action
 * (a `free` pick skips the gate), and REPORTED in the log.
 */
export function placeOnLevel(level, free, { movingToken, levelCosts, writeToken, reportClimbRef }) {
  if (!movingToken) return;
  if (!free && levelCosts[level.top]?.blocked) return;
  const before = movingToken.elevation;
  writeToken({ ...movingToken, elevation: level.top, rev: movingToken.rev + 1 }, { immediate: true });
  reportClimbRef.current?.(movingToken.id, { fromY: before, toY: level.top, free });
}
